/**
 * silenceConsole — removes application-generated noise from the browser
 * DevTools Console in production builds.
 *
 * Why not just `delete console.log`? React/third-party libraries dispatch
 * React-internal warnings via `console.error`, and we still want those for
 * crash telemetry. So we silence only the discretionary methods (log,
 * info, debug, warn) and leave `error` + `assert` alone.
 *
 * This is NOT a security barrier — a determined user can still see
 * network traffic or framework-level errors. It just stops the app from
 * voluntarily printing diagnostic chatter to end-users. The browser's
 * DevTools cannot be programmatically blocked.
 *
 * The gate on `NODE_ENV === 'production'` ensures `yarn start` still
 * shows every log for developers.
 */
export function silenceConsoleInProduction() {
    if (typeof window === 'undefined') return;
    if (process.env.NODE_ENV !== 'production') return;

    // Allow an opt-out so devs on a prod build can still enable logs via
    //   localStorage.setItem('__debugConsole', '1')
    // and refresh. Guards against a hostile localStorage impl throwing.
    try {
        if (window.localStorage?.getItem?.('__debugConsole') === '1') return;
    } catch { /* ignore storage failures */ }

    const noop = () => {};
    const methods = ['log', 'info', 'debug', 'trace', 'warn', 'dir', 'table', 'group', 'groupCollapsed', 'groupEnd', 'count', 'time', 'timeEnd', 'timeLog'];
    for (const m of methods) {
        if (typeof console[m] === 'function') console[m] = noop;
    }
    // Keep `error` and `assert` for runtime diagnostics / Sentry etc.
}
